import React, { useState, useRef, useEffect } from 'react';
import ChatMessage from '../components/AIChat/ChatMessage';
import { aiChatService } from '../services/aiChatService';
import { useNotification } from '../contexts/NotificationContext';
import { useOrganization } from '../contexts/OrganizationContext';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  isThinking?: boolean;
}

const AIChatPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { organization, loading } = useOrganization();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: 'Hello! I\'m your Basilisk assistant. Ask me anything about the Basilisk system, entities, definitions, or development workflows.',
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Determine if Clanker is available based on organization data
  const clankerAvailable = organization?.hasClankerURL === true;

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Show loading state while organization data is being fetched
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading chat configuration...</div>
      </div>
    );
  }

  const addThinkingMessage = () => {
    const thinkingId = `thinking-${Date.now()}`;
    const thinkingMessage: Message = {
      id: thinkingId,
      content: 'Thinking...',
      isUser: false,
      timestamp: new Date(),
      isThinking: true
    };
    setMessages(prev => [...prev, thinkingMessage]);
    return thinkingId;
  };

  const removeThinkingMessage = (thinkingId: string) => {
    setMessages(prev => prev.filter(msg => msg.id !== thinkingId));
  };

  const getAIResponse = async (userMessage: string): Promise<string> => {
    const response = await aiChatService.generateResponse(userMessage);
    return response.content;
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    // Add user message
    const userMsg: Message = {
      id: `user-${Date.now()}`,
      content: userMessage,
      isUser: true,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);

    // Add thinking message
    const thinkingId = addThinkingMessage();

    try {
      // Get AI response from API
      const aiResponse = await getAIResponse(userMessage);
      
      // Remove thinking message and add AI response
      removeThinkingMessage(thinkingId);
      
      const aiMsg: Message = {
        id: `ai-${Date.now()}`,
        content: aiResponse,
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (error: any) {
      // Remove thinking message and add error message
      removeThinkingMessage(thinkingId);
      
      // Show error toast
      const errorMessage = error.message || 'Sorry, there was an error processing your message. Please try again.';
      showNotification(errorMessage, 'error', 5000);
      
      const errorMsg: Message = {
        id: `error-${Date.now()}`,
        content: 'Sorry, there was an error processing your message. Please try again.',
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="p-6 flex-shrink-0">
        <div className="flex items-center max-w-5xl mx-auto">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center">
              <img 
                src="/bsk-asistant-icon.png" 
                alt="BSK Assistant" 
                className="w-12 h-12 rounded-xl"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Basilisk AI Chat
              </h1>
              <p className="text-sm text-gray-600">Your Basilisk system assistant</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto space-y-4">
          {messages.map((message) => (
            <ChatMessage
              key={message.id}
              content={message.content}
              isUser={message.isUser}
              timestamp={message.timestamp}
              isThinking={message.isThinking}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Container */}
      <div className="p-6 flex-shrink-0">
        <div className="max-w-5xl mx-auto">
          {/* Clanker Availability Alert */}
          {clankerAvailable === false && (
            <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    <strong>Basilisk Chat is not active.</strong> The AI service is not configured for this organization. Please contact the administrator to set up the service.
                  </p>
                </div>
              </div>
            </div>
          )}
                      <div className={`flex items-end space-x-4 rounded-2xl border border-gray-200/50 p-4 shadow-lg ${clankerAvailable === false ? 'bg-transparent' : 'bg-white/90 backdrop-blur-sm'}`}>
            <div className="flex-1">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={handleTextareaChange}
                onKeyPress={handleKeyPress}
                placeholder={clankerAvailable === false ? "Chat is disabled - AI service not configured" : "Ask me anything about Basilisk, entities or definitions..."}
                className={`w-full px-0 py-0 border-0 resize-none focus:ring-0 focus:outline-none placeholder-gray-500 'text-gray-900' ${clankerAvailable === false ? 'cursor-not-allowed bg-transparent' : ''}`}
                rows={1}
                style={{ 
                  minHeight: '24px', 
                  maxHeight: '120px',
                  backgroundColor: clankerAvailable === false ? 'transparent' : undefined
                }}
                disabled={clankerAvailable === false}
              />
            </div>
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading || clankerAvailable === false}
              className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl flex items-center justify-center hover:from-green-600 hover:to-emerald-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            {clankerAvailable === false ? "Chat is disabled - AI service not configured" : "Press Enter to send, Shift+Enter for new line"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIChatPage;
